import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Users, BookOpen, Compass, Heart, Ear, Sparkles, ShieldCheck } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

const MODEL = ["See", "Listen", "Explore", "Support", "Honor"];

export default function PortalLandingHosp001() {
  usePageMeta({
    title: "HOSP-001 — See the Person First | CCCHAPS Chaplain Training Academy",
    description: "Free introductory Track Three preview. See the Person. Hear the Voice. Honor the Journey.",
  });
  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-hosp-landing">
      {/* Portal Nav */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/academy/portal" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#4A6FA5] text-white flex items-center justify-center"><GraduationCap className="w-5 h-5" strokeWidth={1.75}/></div>
            <div>
              <div className="font-heading text-slate-900 text-sm font-semibold leading-tight">CCCHAPS</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-tight">Chaplain Training Academy</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/academy/portal/login" data-testid="portal-nav-login" className="text-slate-700 hover:text-[#4A6FA5] font-medium">Sign In</Link>
            <Link to="/academy/portal/register?course=HOSP-001" data-testid="portal-nav-register-hosp" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">Start Free Course <ArrowRight className="w-3.5 h-3.5"/></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3">
            <span data-testid="hosp-badge" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800 mb-4">Book-Based Specialty Track · Free Preview</span>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-3">CCCHAPS Academy · HOSP-001</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              See the Person First
            </h1>
            <p className="mt-3 font-heading text-2xl sm:text-3xl text-slate-700 leading-tight">
              An Introduction to Patient-Centered Hospice Chaplaincy
            </p>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-xl">
              Hospice chaplaincy begins with more than knowing what to say. It begins with learning how to see the end-of-life journey through the patient's eyes.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">
              HOSP-001 introduces the patient-centered philosophy behind <em>From Diagnosis to Goodbye</em> and the CCCHAPS Academy Track Three Hospice Chaplaincy pathway. Participants will explore how presence, listening, patient autonomy, spiritual sensitivity, and thoughtful response shape compassionate hospice chaplaincy.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/academy/portal/register?course=HOSP-001" data-testid="hosp-cta-start-free" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] shadow-sm">
                START FREE COURSE <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/academy/hospice-chaplaincy" data-testid="hosp-cta-explore-track3" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]">
                EXPLORE TRACK THREE
              </Link>
            </div>
          </div>
          <aside className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">At a glance</p>
            <dl className="mt-4 space-y-3 text-sm" data-testid="hosp-at-a-glance">
              {[
                ["Course", "HOSP-001"],
                ["Length", "Approx. 35–45 minutes"],
                ["Level", "Introductory"],
                ["Format", "Online / Self-Paced"],
                ["Cost", "Free"],
                ["Academy Pathway", "Track Three Preview"],
              ].map(([k,v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                  <dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900 text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 rounded-xl bg-[#F0F4F8] p-4">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">HOSP-001 Teaching Model</p>
              <p className="mt-1.5 font-heading text-sm text-slate-900 font-medium" data-testid="hosp-teaching-model">
                {MODEL.join("  →  ")}
              </p>
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 p-4">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Core philosophy</p>
              <p className="mt-1.5 text-sm text-slate-800 leading-relaxed"><strong>See the Person.</strong> <strong>Hear the Voice.</strong> <strong>Honor the Journey.</strong></p>
            </div>
          </aside>
        </div>
      </section>

      {/* Central question */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-16 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">The central question of HOSP-001</p>
          <p className="mt-4 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight leading-snug" data-testid="hosp-central-question">
            Before asking, <em>"What should the chaplain do?"</em> first ask, <em>"What might the patient be experiencing?"</em>
          </p>
        </div>
      </section>

      {/* Course description */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Course description</p>
          <h2 className="mt-2 font-heading text-3xl text-slate-900 tracking-tight">Learning to See Hospice From the Patient's Side of the Bed.</h2>
        </div>
        <div className="lg:col-span-2 space-y-5 text-slate-700 leading-relaxed">
          <p>Hospice professionals often enter a patient's room knowing the diagnosis, decline, medications, care needs, and family concerns. The chaplain must also learn to ask another question: <em>Who is the person experiencing all of this?</em></p>
          <p>HOSP-001 introduces participants to a patient-centered approach to hospice chaplaincy. Rather than beginning with what the chaplain wants to say, pray, teach, or accomplish, participants learn to begin by listening for the patient's voice. The course examines presence, patient autonomy, supportive listening, permission-based spiritual care, and the importance of meeting patients where they are physically, emotionally, relationally, and spiritually.</p>
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="hosp-not-therapy">
            This is introductory chaplaincy education. It is not clinical training, medical instruction, psychotherapy, professional licensure preparation, or accredited continuing education.
          </div>
        </div>
      </section>

      {/* Who should attend */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Who should take HOSP-001</p>
          <h2 className="font-heading text-3xl text-slate-900 tracking-tight max-w-2xl">Appropriate for a range of spiritual-care roles.</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              [BookOpen, "Pastors exploring hospice chaplaincy"],
              [Heart, "Ministers interested in end-of-life spiritual care"],
              [Users, "Current chaplains"],
              [Compass, "Community chaplains"],
              [Sparkles, "Volunteer chaplains"],
              [Ear, "Spiritual-care providers"],
              [GraduationCap, "Chaplaincy students"],
              [BookOpen, "Readers of From Diagnosis to Goodbye"],
              [Compass, "Individuals considering the future Track Three Hospice Chaplaincy pathway"],
            ].map(([Icon, label]) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F0F4F8] text-[#4A6FA5] shrink-0"><Icon className="w-4 h-4" strokeWidth={1.75}/></span>
                <span className="text-sm text-slate-800 leading-relaxed">{label}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-slate-500">No prior CCCHAPS course is required. CTA-001 is not a prerequisite for HOSP-001.</p>
        </div>
      </section>

      {/* Learning objectives */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Learning objectives</p>
          <h2 className="mt-2 font-heading text-3xl text-slate-900 tracking-tight">By completion, participants should be able to:</h2>
        </div>
        <ul className="lg:col-span-2 space-y-2.5 text-slate-700 leading-relaxed text-sm">
          {[
            "Explain why hospice chaplaincy begins with the patient's experience rather than the chaplain's agenda.",
            "Describe the importance of patient autonomy and permission-based spiritual care.",
            "Identify the difference between bringing a pastoral agenda and practicing patient-centered chaplaincy.",
            "Demonstrate basic principles of supportive active listening.",
            "Recognize when silence and presence may be appropriate chaplain interventions.",
            "Identify questions that help explore a patient's emotional and spiritual experience.",
            "Recognize why prayer, scripture, or religious conversation should follow the patient's needs and preferences.",
            "Apply the HOSP-001 model: See → Listen → Explore → Support → Honor.",
            "Describe how From Diagnosis to Goodbye connects patient perspective with practical hospice chaplaincy.",
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-2.5"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4A6FA5] shrink-0"/>{t}</li>
          ))}
        </ul>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">HOSP-001 teaching model</p>
        <p className="mt-3 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight">
          {MODEL.join("  →  ")}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/academy/portal/register?course=HOSP-001" data-testid="hosp-final-cta-start" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] shadow-sm">
            START FREE COURSE <ArrowRight className="w-4 h-4"/>
          </Link>
          <Link to="/academy/hospice-chaplaincy" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]">
            EXPLORE TRACK THREE
          </Link>
        </div>
        <p className="mt-6 text-[11px] text-slate-500 max-w-2xl mx-auto leading-relaxed flex items-start gap-2" data-testid="hosp-disclaimer">
          <ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5] shrink-0 mt-0.5" />
          <span>HOSP-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, hospice certification, or authority to provide medical, mental-health, or clinical treatment.</span>
        </p>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8 text-center text-xs text-slate-500">
        <Link to="/academy" className="hover:text-[#4A6FA5]">← Back to CCCHAPS Chaplain Training Academy</Link>
      </footer>
    </div>
  );
}
